import { useState, useEffect, useRef } from "react";
import SearchIcon from "../../components/assets/searchIcon.svg";
import downloadIcon from "../../components/assets/download-latest-icon.svg";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { CircularProgress } from "@mui/material";
import { ReportServices } from "../../../src/components/services/ReportServices";
import { HospitalServices } from "../../../src/components/services/HospitalServices";
import { handleApiErrorResponses } from "../../components/utils/ApisErrorHandling";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import HospitalCustomPaginaion from "../utils/HospitalPaginationUtil";
import { toast } from "react-toastify";
import downArrow from "../../components/assets/down_arrow.svg";
import MonthRangeDropdown from "../hospitalPersona/hospitalSidebar/revenue/MonthRangeDropDown";

const CcCreditReport = () => {
  const [reportData, setReportData] = useState([]);
  const [filteredReportData, setFilteredReportData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [value, setValue] = useState(10);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [loading, setLoading] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [hospitalList, setHospitalList] = useState([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  const searchTimeRef = useRef();

  const handleSearch = (e) => {
    if (searchTimeRef.current) {
      clearTimeout(searchTimeRef.current);
    }

    searchTimeRef.current = setTimeout(() => {
      setSearchKey(e.target.value?.trim() || "");
    }, 500);

    setPage(1);
  };

  useEffect(() => {
    const getHospitalList = async () => {
      try {
        const response = await HospitalServices.gethospitals("", 1, 1500);

        setHospitalList(response?.data?.list || []);
      } catch (error) {
        handleApiErrorResponses(error);
      }
    };

    getHospitalList();
  }, []);

  const getMonthlyReport = async () => {
    if (!startDate || !endDate || !selectedHospitalId) {
      return;
    }

    setLoading(true);

    try {
      const response = await ReportServices?.getHsaMonthlyClaimReport(
        startDate,
        endDate,
        selectedHospitalId,
      );

      const data = response?.data?.monthlyCaseReport || [];

      setReportData(data);
      setFilteredReportData(data);
      setPage(1);

      const sliced = data.slice(0, limit);

      setPaginatedData(sliced);
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);

    setReportData([]);
    setFilteredReportData([]);
    setPaginatedData([]);

    if (!selectedHospitalId) {
      setStartDate("");
      setEndDate("");
    }
  }, [selectedHospitalId]);

  useEffect(() => {
    if (!startDate || !endDate || !selectedHospitalId) {
      return;
    }

    getMonthlyReport();
  }, [startDate, endDate, selectedHospitalId]);

  useEffect(() => {
    if (!searchKey) {
      setFilteredReportData(reportData);
      setPage(1);

      const sliced = reportData.slice(0, limit);

      setPaginatedData(sliced);

      return;
    }

    const searchValue = searchKey.toLowerCase();

    const modifiedData1 =
      reportData?.filter((data) =>
        data?.patientName?.toLowerCase()?.includes(searchValue),
      ) || [];

    const modifiedData2 =
      reportData?.filter((data) =>
        data?.pmjayRegistrationId?.toLowerCase()?.includes(searchValue),
      ) || [];

    const modifiedData3 =
      reportData?.filter((data) =>
        data?.beneficiaryNo?.toLowerCase()?.includes(searchValue),
      ) || [];

    const merged = [...modifiedData1, ...modifiedData2, ...modifiedData3];

    const unique = Array.from(
      new Map(
        merged.map((item, index) => [
          item?.pmjayRegistrationId || item?.beneficiaryNo || index,
          item,
        ]),
      ).values(),
    );

    setFilteredReportData(unique);
    setPage(1);

    const sliced = unique.slice(0, limit);

    setPaginatedData(sliced);
  }, [searchKey, reportData, limit]);

  useEffect(() => {
    const totalItems = filteredReportData?.length || 0;

    setTotalPages(Math.ceil(totalItems / limit) || 1);
  }, [filteredReportData, limit]);

  useEffect(() => {
    const startIndex = (page - 1) * limit;

    const sliced = filteredReportData.slice(startIndex, startIndex + limit);

    setPaginatedData(sliced);
  }, [page, limit, filteredReportData]);

  useEffect(() => {
    setLimit(value);
    setPage(1);
  }, [value]);

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
      : "NA";

  const generatePdf = async (caseData) => {
    setDownloading(true);

    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const doc = new jsPDF("landscape");

      const headers = [
        "SNo.",
        "Patient Name",
        "Beneficiary ID",
        "Registered ID",
        "Registered Date",
        "Claim Initiated",
        "Claim Approved",
        "TDS",
        "Revenue Credited",
      ];

      const rows = caseData.map((item, index) => [
        index + 1,
        item?.patientName || "NA",
        item?.beneficiaryNo || "NA",
        item?.pmjayRegistrationId || "NA",
        formatDate(item?.registeredDate),
        item?.claimDetails?.claimInitiatedAmount || "NA",
        item?.claimDetails?.claimApprovedAmount || "NA",
        item?.claimDetails?.tdsAmount || "NA",
        item?.claimDetails?.claimPaidAmount || "NA",
      ]);

      autoTable(doc, {
        head: [headers],
        body: rows,
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [230, 230, 230],
          textColor: [0, 0, 0],
        },
        startY: 20,
      });

      doc.save("cc_credit_report.pdf");
    } catch (error) {
      toast.error("Unable to download report.");
    } finally {
      setDownloading(false);
    }
  };

  const generateExcel = async (caseData) => {
    setDownloading(true);

    // Give React time to render the spinner before Excel generation starts
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const rows = caseData.map((item, index) => ({
        "SNo.": index + 1,
        "Patient Name": item?.patientName || "NA",
        "Beneficiary ID": item?.beneficiaryNo || "NA",
        "Registered ID": item?.pmjayRegistrationId || "NA",
        "Registered Date": formatDate(item?.registeredDate),
        "Claim Initiated": item?.claimDetails?.claimInitiatedAmount
          ? `₹ ${item.claimDetails.claimInitiatedAmount}`
          : "NA",
        "Claim Approved": item?.claimDetails?.claimApprovedAmount
          ? `₹ ${item.claimDetails.claimApprovedAmount}`
          : "NA",
        TDS: item?.claimDetails?.tdsAmount
          ? `₹ ${item.claimDetails.tdsAmount}`
          : "NA",
        "Revenue Credited": item?.claimDetails?.claimPaidAmount
          ? `₹ ${item.claimDetails.claimPaidAmount}`
          : "NA",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);

      const columnWidths = Object.keys(rows[0] || {}).map((key) => {
        const headerLength = key.length;

        const maxDataLength = Math.max(
          ...rows.map((row) => String(row[key] ?? "").length),
        );

        return {
          wch: Math.min(Math.max(headerLength, maxDataLength) + 2, 35),
        };
      });

      worksheet["!cols"] = columnWidths;

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Revenue Credited");

      XLSX.writeFile(workbook, "cc_revenue_credited_report.xlsx");
    } catch (error) {
      toast.error("Unable to download Excel report.");
    } finally {
      setDownloading(false);
    }
  };

  const selectedHospital = hospitalList?.find(
    (hospital) => hospital?.id === selectedHospitalId,
  );

  return (
    <div className="w-[calc(100vw-240px)] max-w-none min-w-0 box-border px-6 2xl:px-8 py-4 mt-12 bg-[#F8F8F8] min-h-screen font-[Lufga]">
      {/* Breadcrumb */}
      <div
        style={{
          color: "rgba(0,0,0,0.5)",
        }}
        className="text-[11px] 2xl:text-sm leading-[18.27px] -tracking-[3%]"
      >
        Dashboard / Manage Reports / Revenue Credited
      </div>

      {/* Page Heading */}
      <div className="mt-4">
        <h2 className="leading-[40px] 2xl:leading-[48px] font-medium tracking-[-0.04em] text-[#191919]">
          Revenue Credited ({filteredReportData?.length || 0})
        </h2>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between gap-6 mt-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Hospital Dropdown */}
          <button
            type="button"
            className="flex items-center justify-between border border-[#017C57] rounded-full px-5 py-3 bg-[#F8F8F8] min-w-[220px] relative"
          >
            <select
              value={selectedHospitalId}
              onChange={(e) => {
                setLoading(true);
                setSelectedHospitalId(e.target.value);
                setPage(1);
              }}
              className="absolute inset-0 w-full h-full bg-transparent border-none outline-none ring-0 focus:ring-0 focus:outline-none appearance-none cursor-pointer text-[#017C57] text-xs 2xl:text-sm font-medium pl-5 pr-12 opacity-0 z-10"
            >
              <option value="">HOSPITAL NAME</option>

              {hospitalList?.map((hospital) => (
                <option key={hospital?.id} value={hospital?.id}>
                  {hospital?.name || hospital?.code}
                </option>
              ))}
            </select>

            <span className="text-[#017C57] text-xs 2xl:text-sm font-medium truncate pr-2 flex-1 text-left">
              {selectedHospitalId
                ? selectedHospital?.name ||
                  selectedHospital?.code ||
                  "HOSPITAL NAME"
                : "HOSPITAL NAME"}
            </span>

            <img src={downArrow} alt="dropdown" className="w-4 h-4 shrink-0" />
          </button>

          {/* Existing CC Revenue Month Dropdown */}
          {/* From Date */}
          <div className="flex items-center gap-2 border border-solid border-[#017C57] rounded-full px-5 py-3 bg-[#F8F8F8]">
            <span className="text-[#017C57] text-xs 2xl:text-sm font-medium whitespace-nowrap">
              FROM
            </span>

            <input
              type="date"
              value={
                startDate ? new Date(startDate).toISOString().split("T")[0] : ""
              }
              onChange={(e) => {
                if (!e.target.value) {
                  setStartDate("");
                  setPage(1);
                  return;
                }

                const selectedDate = new Date(
                  `${e.target.value}T00:00:00.000Z`,
                );

                setStartDate(selectedDate.toISOString());
                setPage(1);
              }}
              className="bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-[#191919] text-xs 2xl:text-sm font-medium cursor-pointer"
            />
          </div>

          {/* To Date */}
          <div className="flex items-center gap-2 border border-solid border-[#017C57] rounded-full px-5 py-3 bg-[#F8F8F8]">
            <span className="text-[#017C57] text-xs 2xl:text-sm font-medium whitespace-nowrap">
              TO
            </span>

            <input
              type="date"
              value={
                endDate ? new Date(endDate).toISOString().split("T")[0] : ""
              }
              onChange={(e) => {
                if (!e.target.value) {
                  setEndDate("");
                  setPage(1);
                  return;
                }

                const selectedDate = new Date(
                  `${e.target.value}T23:59:59.999Z`,
                );

                setEndDate(selectedDate.toISOString());
                setPage(1);
              }}
              className="bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-[#191919] text-xs 2xl:text-sm font-medium cursor-pointer"
            />
          </div>
        </div>

        {/* Download */}
        <div className="relative shrink-0">
          {/* Download Button */}
          <button
            type="button"
            disabled={downloading}
            onClick={() => {
              if (!downloading) {
                setShowDownloadMenu((prev) => !prev);
              }
            }}
            className="flex items-center justify-center gap-2 border border-none border-[#017C71] rounded-full px-5 py-[13px] bg-[#017C71] text-white text-sm font-medium cursor-pointer min-w-[180px] disabled:cursor-not-allowed"
          >
            {downloading ? (
              <CircularProgress
                size={18}
                thickness={5}
                sx={{
                  color: "#FFFFFF",
                }}
              />
            ) : (
              <>
                <FileDownloadOutlinedIcon
                  sx={{
                    fontSize: 19,
                  }}
                />

                <span>Download Report</span>

                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`transition-transform duration-200 ${
                    showDownloadMenu ? "rotate-180" : ""
                  }`}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </>
            )}
          </button>

          {/* Download Dropdown */}
          {showDownloadMenu && (
            <div className="absolute right-0 top-full mt-0 w-full min-w-[180px] bg-white border border-[#AFAFAF] rounded-b-[10px] rounded-t-none overflow-hidden z-50 shadow-sm">
              <button
                type="button"
                disabled={downloading}
                onClick={() => {
                  setShowDownloadMenu(false);

                  if (filteredReportData?.length > 0) {
                    generatePdf(filteredReportData);
                  } else {
                    toast.info("No data present to generate pdf.");
                  }
                }}
                className="w-full text-left px-4 py-3 text-sm text-[#191919] bg-white hover:bg-[#F8F8F8] border-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                Download PDF
              </button>

              <button
                type="button"
                disabled={downloading}
                onClick={() => {
                  setShowDownloadMenu(false);

                  if (filteredReportData?.length > 0) {
                    generateExcel(filteredReportData);
                  } else {
                    toast.info("No data present to generate excel.");
                  }
                }}
                className="w-full text-left px-4 py-3 text-sm text-[#191919] bg-white hover:bg-[#F8F8F8] border-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                Download Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div
        id="case-list"
        className="w-full min-w-0 p-4 2xl:p-6 mt-4 rounded-[24px] bg-[#FCFCFC]"
      >
        {/* <div className="w-full min-w-0 bg-white p-[24px] 2xl:p-[30px] rounded-[15px] mt-[4px]"> */}
        {/* Table Header Controls */}
        <div className="flex items-center justify-between gap-6 mb-4">
          {/* Section Title */}
          <div className="3xl:text-[18px] 2xl:text-sm leading-[18px] -tracking-[3%] text-[#191919] font-semibold">
            Revenue Credited Report
          </div>

          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="flex items-center gap-3 border border-solid border-[#017C57] rounded-full box-border px-5 py-3  xl:w-[380px] 2xl:w-[420px] 3xl:w-[440px]">
              <img
                src={SearchIcon}
                alt="search-icon"
                className="w-4 h-4 shrink-0"
              />

              <input
                type="text"
                placeholder="Search patient name, beneficiary number, registered id"
                className="w-full bg-transparent outline-none border-none text-[#191919] text-xs 2xl:text-sm font-medium placeholder:text-[#191919]"
                onChange={handleSearch}
              />
            </div>

            {/* Number Of Cases */}
            <div className="flex items-center gap-2">
              <span className="text-xs 2xl:text-sm text-[#555] uppercase tracking-[0.8px] whitespace-nowrap">
                NUMBER OF CASES
              </span>

              <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-solid border-[#017C57] w-fit cursor-pointer bg-white">
                <select
                  value={value}
                  onChange={(e) => setValue(Number(e.target.value))}
                  className="bg-transparent outline-none cursor-pointer appearance-none border-none pl-1 pr-6 text-xs 2xl:text-sm"
                >
                  {[10, 25, 50].map((number) => (
                    <option key={number} value={number}>
                      {number}
                    </option>
                  ))}
                </select>

                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="pointer-events-none -ml-4"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="w-full min-w-0 overflow-x-auto">
          <div className="min-w-[1050px]">
            {/* Column Heading */}
            <div className="grid grid-cols-[0.5fr_1.5fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-4 text-[12px] 2xl:text-sm py-1 items-center mt-[8px] 2xl:mt-[10px] first:mt-6 2xl:first:mt-8 bg-gradient-to-r from-white via-white to-[#F9F9F9] text-[#191919]">
              <div>SNo.</div>

              <div>Patient Name</div>

              <div>Registered ID</div>

              <div>Registered Date</div>

              <div>Claim Initiated</div>

              <div>Claim Approved</div>

              <div>TDS</div>

              <div>Revenue Credited</div>
            </div>

            {/* Loading */}
            {loading ? (
              <div className="h-[200px] flex justify-center items-center">
                <CircularProgress />
              </div>
            ) : paginatedData?.length > 0 ? (
              paginatedData.map((reportItem, idx) => (
                <div
                  key={reportItem?.pmjayRegistrationId || idx}
                  className="grid grid-cols-[0.5fr_1.5fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-4 text-[12px] 2xl:text-sm py-1 items-center mt-[8px] 2xl:mt-[10px] first:mt-6 2xl:first:mt-8 bg-white text-[#191919]"
                >
                  {/* SNo */}
                  <div>{(page - 1) * limit + idx + 1}</div>

                  {/* Patient Name */}
                  <div>
                    <div>{reportItem?.patientName || "NA"}</div>

                    <div className="text-[11px] text-[#00000080] -tracking-[0.03em]">
                      {reportItem?.beneficiaryNo || "NA"}
                    </div>
                  </div>

                  {/* Registered ID */}
                  <div
                    className="cursor-text select-all text-ellipsis text-nowrap overflow-hidden"
                    title={reportItem?.pmjayRegistrationId}
                  >
                    {reportItem?.pmjayRegistrationId || "NA"}
                  </div>

                  {/* Registered Date */}
                  <div>{formatDate(reportItem?.registeredDate)}</div>

                  {/* Claim Initiated */}
                  <div>
                    {reportItem?.claimDetails?.claimInitiatedAmount
                      ? `₹ ${reportItem.claimDetails.claimInitiatedAmount}`
                      : "NA"}
                  </div>

                  {/* Claim Approved */}
                  <div>
                    {reportItem?.claimDetails?.claimApprovedAmount
                      ? `₹ ${reportItem.claimDetails.claimApprovedAmount}`
                      : "NA"}
                  </div>

                  {/* TDS */}
                  <div>
                    {reportItem?.claimDetails?.tdsAmount
                      ? `₹ ${reportItem.claimDetails.tdsAmount}`
                      : "NA"}
                  </div>

                  {/* Revenue Credited */}
                  <div>
                    {reportItem?.claimDetails?.claimPaidAmount
                      ? `₹ ${reportItem.claimDetails.claimPaidAmount}`
                      : "NA"}
                  </div>
                </div>
              ))
            ) : (
              <div className="h-[200px] flex justify-center items-center font-semibold text-xl text-[#191919]">
                {!selectedHospitalId
                  ? "Please Select Hospital"
                  : "No Case Data Found"}
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {filteredReportData?.length > 0 && (
          <div className="flex justify-center mt-[12px] 2xl:mt-[15px]">
            <HospitalCustomPaginaion
              page={page}
              setPage={setPage}
              totalPages={totalPages}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CcCreditReport;
